import { createContext, useMemo, useState } from "react";
import { createTheme } from "@mui/material/styles";

export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#f7f7f8",
          200: "#e7e7ea",
          300: "#c8c9cf",
          400: "#9a9da6",
          500: "#737782",
          600: "#4b4f5b",
          700: "#30343d",
          800: "#1b1e25",
          900: "#0b0d12",
        },
        primary: {
          100: "#f43f46",
          200: "#e03138",
          300: "#c42831",
          400: "#9f1f27",
          500: "#0f1117",
          600: "#131720",
          700: "#1a1f2a",
          800: "#090b10",
          900: "#ffffff",
        },
        greenAccent: {
          100: "#e9fbf4",
          200: "#bdf1dc",
          300: "#7fe0ba",
          400: "#36c18f",
          500: "#16a374",
          600: "#0f805b",
          700: "#0c6147",
          800: "#073f30",
          900: "#04261e",
        },
        redAccent: {
          100: "#fff0f1",
          200: "#ffd6d9",
          300: "#ff9fa5",
          400: "#fb6870",
          500: "#f43f46",
          600: "#d72c34",
          700: "#ad2229",
          800: "#73191e",
          900: "#3d0d10",
        },
        blueAccent: {
          100: "#f0f7ff",
          200: "#d5e9ff",
          300: "#9ed0ff",
          400: "#5baeff",
          500: "#2f8df0",
          600: "#1f6fc4",
          700: "#1a5798",
          800: "#143b68",
          900: "#0d243f",
        },
      }
    : {
        grey: {
          100: "#111318",
          200: "#242832",
          300: "#3c414d",
          400: "#666c78",
          500: "#828895",
          600: "#a7acb7",
          700: "#ced2da",
          800: "#eceef3",
          900: "#f8f9fb",
        },
        primary: {
          100: "#d92d36",
          200: "#b4232c",
          300: "#8f1d24",
          400: "#64151a",
          500: "#f8f9fb",
          600: "#ffffff",
          700: "#f1f3f7",
          800: "#e6e8ee",
          900: "#111318",
        },
        greenAccent: {
          100: "#04261e",
          200: "#073f30",
          300: "#0c6147",
          400: "#0f805b",
          500: "#16a374",
          600: "#36c18f",
          700: "#7fe0ba",
          800: "#bdf1dc",
          900: "#e9fbf4",
        },
        redAccent: {
          100: "#3d0d10",
          200: "#73191e",
          300: "#ad2229",
          400: "#d72c34",
          500: "#f43f46",
          600: "#fb6870",
          700: "#ff9fa5",
          800: "#ffd6d9",
          900: "#fff0f1",
        },
        blueAccent: {
          100: "#0d243f",
          200: "#143b68",
          300: "#1a5798",
          400: "#1f6fc4",
          500: "#2f8df0",
          600: "#5baeff",
          700: "#9ed0ff",
          800: "#d5e9ff",
          900: "#f0f7ff",
        },
      }),
});

export const themeSettings = (mode) => {
  const colors = tokens(mode);
  const isDark = mode === "dark";

  return {
    palette: {
      mode,
      primary: { main: colors.primary[100] },
      secondary: { main: colors.greenAccent[500] },
      neutral: {
        dark: colors.grey[700],
        main: colors.grey[500],
        light: colors.grey[100],
      },
      background: {
        default: isDark ? "#0f1117" : "#f8f9fb",
        paper: isDark ? "#131720" : "#ffffff",
      },
    },
    typography: {
      fontFamily: ["Inter", "Poppins", "sans-serif"].join(","),
      fontSize: 12,
      h1: { fontSize: 40, fontWeight: 800, letterSpacing: 0 },
      h2: { fontSize: 30, fontWeight: 800, letterSpacing: 0 },
      h3: { fontSize: 24, fontWeight: 750, letterSpacing: 0 },
      h4: { fontSize: 19, fontWeight: 700, letterSpacing: 0 },
      h5: { fontSize: 16, fontWeight: 700, letterSpacing: 0 },
      h6: { fontSize: 14, fontWeight: 700, letterSpacing: 0 },
      button: { fontWeight: 700, letterSpacing: 0, textTransform: "none" },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: "none",
            textTransform: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(17,19,24,0.08)"}`,
          },
        },
      },
      MuiTextField: {
        defaultProps: { size: "small" },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
    },
  };
};

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);

  return [theme, colorMode];
};
